import { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Row,
  Col,
  Typography,
  Button,
  Tag,
  Avatar,
  Dropdown,
  Space,
  message,
  Spin,
  Progress,
  Breadcrumb
} from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  HomeOutlined,
  ArrowLeftOutlined,
  ExperimentOutlined,
  LockOutlined,
  UnlockOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';

const { Header, Content } = Layout;
const { Title, Text, Paragraph } = Typography;

export default function TrainingDetail() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { trainingId } = useParams();
  const [loading, setLoading] = useState(true);
  const [training, setTraining] = useState(null);
  const [labs, setLabs] = useState([]);

  useEffect(() => {
    fetchTrainingData();
  }, [trainingId]);

  const fetchTrainingData = async () => {
    try {
      setLoading(true);
      const [trainingRes, labsRes] = await Promise.allSettled([
        api.get(`/student/trainings/${trainingId}`),
        api.get(`/student/trainings/${trainingId}/labs`)
      ]);

      if (trainingRes.status === 'fulfilled') {
        setTraining(trainingRes.value.data);
      }

      if (labsRes.status === 'fulfilled') {
        setLabs(labsRes.value.data || []);
      }
    } catch (error) {
      message.error('Failed to load training details');
      console.error('Error fetching training:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartLab = async (labId) => {
    try {
      await api.post(`/student/labs/${labId}/start`);
      navigate(`/student/lab/${labId}`);
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to start lab');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: <HomeOutlined />,
      onClick: () => navigate('/student/dashboard'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      danger: true,
      onClick: handleLogout,
    },
  ];

  const getLabStatusIcon = (status) => {
    const icons = {
      locked: <LockOutlined />,
      available: <UnlockOutlined />,
      in_progress: <ClockCircleOutlined />,
      completed: <CheckCircleOutlined />
    };
    return icons[status] || <LockOutlined />;
  };

  const getLabStatusColor = (status) => {
    const colors = {
      locked: 'default',
      available: 'blue',
      in_progress: 'orange',
      completed: 'green'
    };
    return colors[status] || 'default';
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: '#fff',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Title level={3} style={{ margin: 0, color: '#faad14', cursor: 'pointer' }} onClick={() => navigate('/student/dashboard')}>
            USSEDU Platform
          </Title>
          <Text type="secondary">Training Details</Text>
        </div>
        <Space>
          <Text strong>{user?.email}</Text>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Avatar style={{ backgroundColor: '#faad14', cursor: 'pointer' }} icon={<UserOutlined />} />
          </Dropdown>
        </Space>
      </Header>

      <Content style={{ padding: '24px', background: '#f0f2f5' }}>
        <Breadcrumb style={{ marginBottom: 16 }}>
          <Breadcrumb.Item href="#" onClick={() => navigate('/student/dashboard')}>
            <HomeOutlined /> Dashboard
          </Breadcrumb.Item>
          <Breadcrumb.Item>Training Details</Breadcrumb.Item>
        </Breadcrumb>

        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/student/dashboard')}
          style={{ marginBottom: 16 }}
        >
          Back to Dashboard
        </Button>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Spin size="large" />
          </div>
        ) : training ? (
          <>
            {/* Training Info Card */}
            <Card style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                <Col xs={24} md={18}>
                  <Title level={2} style={{ marginTop: 0 }}>{training.name}</Title>
                  <Paragraph>{training.description}</Paragraph>
                  <Space wrap>
                    <Tag color="blue">{training.type || 'Training'}</Tag>
                    <Tag color={getLabStatusColor(training.status)} icon={getLabStatusIcon(training.status)}>
                      {training.status?.toUpperCase().replace('_', ' ')}
                    </Tag>
                  </Space>
                </Col>
                <Col xs={24} md={6}>
                  <Card size="small">
                    <div style={{ textAlign: 'center' }}>
                      <Text type="secondary">Progress</Text>
                      <Progress
                        type="circle"
                        percent={Math.round(((training.completed_labs || 0) / (training.total_labs || 1)) * 100)}
                        strokeColor="#faad14"
                      />
                      <div style={{ marginTop: 8 }}>
                        <Text strong>{training.completed_labs || 0}/{training.total_labs || 0}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>Labs Completed</Text>
                      </div>
                    </div>
                  </Card>
                </Col>
              </Row>
            </Card>

            {/* Labs List */}
            <Card title="Labs">
              <Row gutter={[16, 16]}>
                {labs.length === 0 ? (
                  <Col span={24}>
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                      <ExperimentOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                      <Paragraph type="secondary">No labs available in this training</Paragraph>
                    </div>
                  </Col>
                ) : (
                  labs.map((lab, index) => (
                    <Col xs={24} md={12} lg={8} key={lab.id}>
                      <Card
                        hoverable={lab.status === 'available'}
                        style={{
                          height: '100%',
                          opacity: lab.status === 'locked' ? 0.6 : 1
                        }}
                      >
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Avatar size="small" style={{ backgroundColor: '#faad14' }}>
                                {index + 1}
                              </Avatar>
                              <Title level={5} style={{ margin: 0 }}>{lab.name}</Title>
                            </div>
                            <Tag color={getLabStatusColor(lab.status)} icon={getLabStatusIcon(lab.status)}>
                              {lab.status?.toUpperCase().replace('_', ' ')}
                            </Tag>
                          </div>

                          <Paragraph
                            type="secondary"
                            ellipsis={{ rows: 3 }}
                            style={{ marginBottom: 0 }}
                          >
                            {lab.description}
                          </Paragraph>

                          <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Duration: <strong>{lab.estimated_duration_minutes || 'N/A'} min</strong>
                            </Text>
                          </div>

                          {lab.status === 'available' && (
                            <Button
                              type="primary"
                              block
                              onClick={() => handleStartLab(lab.id)}
                            >
                              Start Lab
                            </Button>
                          )}

                          {lab.status === 'in_progress' && (
                            <Button
                              type="default"
                              block
                              onClick={() => navigate(`/student/lab/${lab.id}`)}
                            >
                              Continue Lab
                            </Button>
                          )}

                          {lab.status === 'completed' && lab.evaluation_score !== undefined && (
                            <div>
                              <Text type="secondary" style={{ fontSize: 12 }}>Score:</Text>
                              <Progress
                                percent={Math.round(lab.evaluation_score)}
                                size="small"
                                status={lab.evaluation_score >= 80 ? 'success' : 'normal'}
                              />
                            </div>
                          )}

                          {lab.status === 'locked' && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Complete previous labs to unlock
                            </Text>
                          )}
                        </Space>
                      </Card>
                    </Col>
                  ))
                )}
              </Row>
            </Card>
          </>
        ) : (
          <Card>
            <Paragraph>Training not found</Paragraph>
          </Card>
        )}
      </Content>
    </Layout>
  );
}
